import Banner from "~/components/banner"
import Circuit from "~/components/circuit"
import Pathways from "./pathways"
import Pathways2 from "./pathways2"
import Pathways3 from "./pathways3"
import Pathways4 from "./pathways4"
import Figure from "~/components/figure"
import SvgFigure from "~/components/svgFigure"

export default class OrientedDogs extends Component {
  render() {
    return (
      <React.Fragment>
        <Banner
          title="Oriented Dogs"
          subtitle={
            <span>
              The oriented dog circuit has two almost perfectly symmetrical
              pathways for left-oriented and right-oriented dog neurons.
              Disabling a pathway alters specific model behavior.
            </span>
          }
          figure={
            <Figure margin={0}>
              <Circuit
                inlineLayerNames
                circuitProps={{ moduleWidth: "auto", moduleMarginX: 80 }}
                circuit={[
                  {
                    name: "mixed3b",
                    modules: [
                      {
                        name: "Left",
                        neurons: [333],
                      },
                      {
                        name: "Right",
                        neurons: [404],
                      },
                    ],
                  },
                  {
                    name: "mixed4a",
                    modules: [
                      {
                        name: "Left",
                        neurons: [222, 325],
                      },
                      {
                        name: "Right",
                        neurons: [308, 234],
                      },
                      {
                        name: "Invariant",
                        neurons: [268],
                      },
                    ],
                  },
                  {
                    name: "mixed4b",
                    modules: [
                      {
                        name: "Left",
                        neurons: [429, 384],
                      },
                      {
                        name: "Right",
                        neurons: [443, 431],
                      },
                      {
                        name: "Invariant",
                        neurons: [409, 418],
                      },
                    ],
                  },
                  {
                    name: "mixed4c",
                    modules: [
                      {
                        name: "Invariant",
                        neurons: [178, 346],
                      },
                    ],
                  },
                ]}
              />
            </Figure>
          }
        />

        <Heading>Abstract</Heading>
        <Paragraph>
          The creators of the ImageNet dataset wanted to over-emphasize a single
          category of image, forcing models to distinguish between fine-details
          of objects to selectively classify them. They decided on dogs and
          included 90 breeds in the dataset, almost 10% of the total classes. As
          a result, many of the neurons in ImageNet models focus on dog related
          objects.
        </Paragraph>
        <Paragraph>
          In this article we’ll be looking at a circuit we call the oriented dog
          circuit of InceptionV1 that keeps track of dog head orientation. We’ll
          look at how the network implements neurons that select for specific
          orientations and others that are orientation-invariant. We’ll also use
          circuit editing to see how the overall model’s behavior is changed by
          disabling specific orientations.
        </Paragraph>
        <Heading>Two Symmetrical Pathways</Heading>
        <Paragraph>
          The oriented dog circuit has two almost perfectly symmetrical pathways
          for <b>left-oriented</b> and <b>right-oriented</b> neurons. At each
          step, the two pathways inhibit each other and excite the next stage.
        </Paragraph>
        <Paragraph>
          Let's zoom in and look at how the oriented fur neurons in 3b connect
          to the oriented head neurons in 4a.
        </Paragraph>
        <Pathways />
        <Paragraph>
          When we compare the weights from the left oriented neurons in 3b to
          the right oriented neurons in the same layer, we see that they are
          nearly mirror each other. Each orientation excites the similar
          orientation in 4a and inhbits the other.
        </Paragraph>
        <Paragraph>
          If we look at the connection between the oriented head neurons in 4a
          and the head and neck neurons in 4b we see the same pattern.
        </Paragraph>
        <Pathways2 />
        <Paragraph>
          We can also see that the oriented neck and head neurons are activated
          and inhibited primarily in the direction they are facing.
        </Paragraph>
        <Paragraph>
          In order to understand the spatial of the oriented head and neck
          neurons in 4b more deeply, we can look at the largest weight
          magnitudes from 4a going into 4b:429.
        </Paragraph>
        <Pathways3 />
        <Paragraph>
          We see that all the top weights going into 4b:429 except 4a:290 are
          excited only on the left side of the convolutional filter. The one
          exception (4a:290) is a boundary detection unit. We call these neurons
          that primarily exist to transform neurons spatially shifting neurons.
          We usually find them in the 5x5 branches of InceptionV1, which is the
          largest convolutional filter size found in the model.
        </Paragraph>
        <Heading>Orientation-Invariant Neurons</Heading>
        <Paragraph>
          In addition to oriented neurons, layers 4b and 4c have
          orientation-invariant neurons that are excited by both orientations in
          the previous layer. We call this a union motif.
        </Paragraph>
        <Pathways4 />
        <Paragraph>
          Putting these sub-circuits together recovers the full oriented dog
          circuit.
        </Paragraph>
        <Figure>
          <Circuit
            inlineLayerNames
            circuitProps={{ moduleWidth: "auto", moduleMarginX: 80 }}
            circuit={[
              {
                name: "mixed3b",
                modules: [
                  {
                    name: "Left",
                    neurons: [333],
                  },
                  {
                    name: "Right",
                    neurons: [404],
                  },
                ],
              },
              {
                name: "mixed4a",
                modules: [
                  {
                    name: "Left",
                    neurons: [222, 325],
                  },
                  {
                    name: "Right",
                    neurons: [308, 234],
                  },
                  {
                    name: "Invariant",
                    neurons: [268],
                  },
                ],
              },
              {
                name: "mixed4b",
                modules: [
                  {
                    name: "Left",
                    neurons: [429, 384],
                  },
                  {
                    name: "Right",
                    neurons: [443, 431],
                  },
                  {
                    name: "Invariant",
                    neurons: [409, 418],
                  },
                ],
              },
              {
                name: "mixed4c",
                modules: [
                  {
                    name: "Invariant",
                    neurons: [178, 346],
                  },
                ],
              },
            ]}
          />
        </Figure>
        <Heading>Using Circuit Editing to Remove Orientations</Heading>
        <Paragraph>Coming soon</Paragraph>
      </React.Fragment>
    )
  }
}
